import { computed, shallowRef, watch } from "vue"

import { errorMessage } from "@/lib/errors"
import { postJson } from "@/lib/fetch"
import { formatsFor } from "@/lib/printing"
import { previewLabelsPath, printLabelsPath } from "@/routes"
import type { PrintItem } from "@/types/models"
import type { PrintFormat } from "@/types/printing"

interface LabelPrintingOptions {
  item: PrintItem | null
  formats: PrintFormat[]
  initialText?: string
  initialQr?: boolean
  initialStyle?: string
}

export function useLabelPrinting(options: LabelPrintingOptions) {
  const item = shallowRef<PrintItem | null>(options.item ? { ...options.item } : null)
  const formats = formatsFor(options.formats, "item")
  const text = shallowRef(options.initialText ?? options.item?.name ?? "")
  const includeQr = shallowRef(options.initialQr ?? Boolean(options.item))
  const style = shallowRef(options.initialStyle ?? "label")
  const quantity = shallowRef(1)
  const printStatus = shallowRef<"idle" | "printing" | "printed" | "error">("idle")
  const printMessage = shallowRef("")

  const availableFormats = computed(() =>
    item.value ? formats : formats.filter((format) => format.key === "label"),
  )
  const selectedFormat = computed(
    () => formats.find((format) => format.key === style.value) ?? formats[0],
  )
  const isFull = computed(() => selectedFormat.value?.key === "label")
  const canPrint = computed(() => {
    if (!isFull.value) return Boolean(item.value)
    return text.value.trim().length > 0
  })
  const previewUrl = computed(() => {
    if (!canPrint.value) return null

    const params = requestParams()
    params.delete("quantity")
    return `${previewLabelsPath()}?${params.toString()}`
  })

  watch([text, includeQr, quantity, style], resetStatus)

  function setStyle(value: string) {
    if (availableFormats.value.some((format) => format.key === value)) style.value = value
  }

  function setText(value: string) {
    text.value = value
  }

  function setIncludeQr(value: boolean) {
    includeQr.value = value
  }

  function setQuantity(value: number) {
    quantity.value = value
  }

  function clearItem() {
    item.value = null
    style.value = "label"
  }

  function reset() {
    text.value = item.value?.name ?? ""
    includeQr.value = Boolean(item.value)
    quantity.value = 1
    style.value = item.value?.is_container ? "label" : item.value ? "equipment" : "label"
    resetStatus()
  }

  async function print() {
    if (printStatus.value === "printing" || !canPrint.value) return
    printStatus.value = "printing"
    printMessage.value = ""

    try {
      const body: Record<string, unknown> = {
        style: style.value,
        quantity: quantity.value,
      }
      if (item.value) body.item_id = item.value.id
      if (isFull.value) {
        body.text = text.value.trim()
        body.include_qr = includeQr.value ? "1" : "0"
      }

      const response = await postJson(printLabelsPath(), body)
      const data = await response.json()

      if (data.status === "printed") {
        if (item.value && data.asset_code) {
          item.value = { ...item.value, asset_code: data.asset_code }
        }
        printStatus.value = "printed"
        printMessage.value =
          data.quantity > 1 ? `${data.quantity} labels printed!` : "Label printed!"
      } else {
        printStatus.value = "error"
        printMessage.value = data.message || "Print failed"
      }
    } catch (error) {
      printStatus.value = "error"
      printMessage.value = errorMessage(error, "Print failed")
    }
  }

  function requestParams(): URLSearchParams {
    const params = new URLSearchParams({
      style: style.value,
      quantity: String(quantity.value),
    })
    if (item.value) params.set("item_id", item.value.id)
    if (isFull.value) {
      params.set("text", text.value.trim())
      params.set("include_qr", includeQr.value ? "1" : "0")
    }
    return params
  }

  function resetStatus() {
    printStatus.value = "idle"
    printMessage.value = ""
  }

  return {
    item,
    formats: availableFormats,
    text,
    includeQr,
    style,
    quantity,
    printStatus,
    printMessage,
    selectedFormat,
    isFull,
    previewUrl,
    canPrint,
    setStyle,
    setText,
    setIncludeQr,
    setQuantity,
    clearItem,
    reset,
    print,
  }
}
